import { useEffect, useRef, useState } from 'react'
import { Avatar, Button, FileButton, Group, Stack, Text } from '@mantine/core'
import { IconUpload } from '@tabler/icons-react'
import type { PractitionerPhoto } from '@behuman/core-contract'
import { ACCENT } from '@/components/ui/primaryButton.tokens'
import { useBinaryObjectUrl } from '@/hooks/useBinaryObjectUrl'
import { PHOTO_TYPES, photoProblem } from '@/utils/practitionerPhoto'

/**
 * The Profile tab's photo block: the Org Member's photo, or their initials when there is
 * none, with Add (Replace once there is a photo) and Remove. A picked file shows at once and
 * rides on the form until Save turns it into a Binary. The stored url is the canonical
 * `Binary/<id>`, which only the viewer's Medplum client can resolve, so it is downloaded and
 * shown through an object URL.
 */
export function PhotoField({
  name,
  photo,
  pendingFile,
  onPick,
  onRemove,
}: {
  name: string
  photo: PractitionerPhoto | null | undefined
  pendingFile: File | null | undefined
  onPick: (file: File) => void
  onRemove: () => void
}) {
  const storedUrl = useBinaryObjectUrl(photo?.url)
  const [pendingUrl, setPendingUrl] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const resetPicker = useRef<() => void>(null)

  useEffect(() => {
    if (!pendingFile) {
      setPendingUrl(null)
      return
    }
    const url = URL.createObjectURL(pendingFile)
    setPendingUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [pendingFile])

  const hasPhoto = Boolean(pendingFile || photo)

  return (
    <Group align="center" gap="md">
      <Avatar
        src={pendingUrl ?? storedUrl}
        alt="Photo"
        name={name}
        color="#275587"
        size={72}
        radius={9999}
      />
      <Stack gap={4}>
        <Group gap="xs">
          <FileButton
            accept={PHOTO_TYPES.join(',')}
            resetRef={resetPicker}
            inputProps={{ 'aria-label': 'Photo file' }}
            onChange={(file) => {
              // Reset so picking the same file again after a Remove fires onChange.
              resetPicker.current?.()
              if (!file) return
              const rejected = photoProblem(file)
              setProblem(rejected)
              if (!rejected) onPick(file)
            }}
          >
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="subtle"
                color={ACCENT}
                leftSection={<IconUpload size={14} />}
              >
                {hasPhoto ? 'Replace photo' : 'Add photo'}
              </Button>
            )}
          </FileButton>
          {hasPhoto && (
            <Button
              size="xs"
              variant="subtle"
              color="red"
              onClick={() => {
                setProblem(null)
                onRemove()
              }}
            >
              Remove photo
            </Button>
          )}
        </Group>
        <Text size="xs" c="dimmed">
          JPEG, PNG or WebP · up to 5 MB
        </Text>
        {problem && (
          <Text size="xs" c="red">
            {problem}
          </Text>
        )}
      </Stack>
    </Group>
  )
}
